import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, parseISO, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useSubscription } from '../../context/SubscriptionContext';

const { FiPlay, FiPause, FiSkipBack, FiSkipForward, FiCalendar, FiChevronLeft, FiChevronRight, FiZap, FiLock } = FiIcons;

const SPEEDS = [
  { label: '0.5×', ms: 5000 },
  { label: '1×', ms: 3000 },
  { label: '2×', ms: 1600 },
  { label: '4×', ms: 800 },
];

// Fade takes ~55% of the frame duration, capped for slow speeds
const getFadeDuration = (speedMs) => Math.min(speedMs * 0.55 / 1000, 2.0);

function DateInput({ label, value, onChange, min, max }) {
  return (
    <div className="flex-1">
      <p className="font-sans text-xs font-semibold text-stone-400 uppercase tracking-widest mb-1.5">{label}</p>
      <input type="date" value={value} min={min} max={max} onChange={e => onChange(e.target.value)}
        className="w-full bg-white border border-stone-200 rounded-2xl px-3 py-2.5 text-sm font-sans text-stone-700 focus:outline-none appearance-none" />
    </div>
  );
}

// Imperative two-slot dissolve. Reads the latest fadeDuration via a ref so
// speed changes take effect immediately without re-subscribing the effect.
// Uses the image onload event (falling back to double-rAF for cached images)
// to guarantee the incoming frame is painted before the crossfade begins.
function DissolveImage({ src, fadeDuration }) {
  const aRef = useRef(null);
  const bRef = useRef(null);
  const activeSlot = useRef('a');
  const initialised = useRef(false);
  const fadeRef = useRef(fadeDuration);
  fadeRef.current = fadeDuration;

  useEffect(() => {
    if (!src) return;

    if (!initialised.current) {
      initialised.current = true;
      const el = aRef.current;
      if (!el) return;
      el.style.transition = 'none';
      el.style.opacity = '0';
      const show = () => { el.style.opacity = '1'; };
      el.src = src;
      if (el.complete && el.naturalWidth > 0) {
        requestAnimationFrame(show);
      } else {
        el.onload = show;
      }
      return;
    }

    const entering = activeSlot.current === 'a' ? bRef.current : aRef.current;
    const leaving  = activeSlot.current === 'a' ? aRef.current : bRef.current;
    if (!entering || !leaving) return;

    entering.style.transition = 'none';
    entering.style.opacity = '0';

    const doFade = () => {
      const t = `opacity ${fadeRef.current}s ease-in-out`;
      entering.style.transition = t;
      leaving.style.transition = t;
      entering.style.opacity = '1';
      leaving.style.opacity = '0';
      activeSlot.current = activeSlot.current === 'a' ? 'b' : 'a';
    };

    entering.src = src;
    if (entering.complete && entering.naturalWidth > 0) {
      // Already cached — wait two frames so the new src paints at opacity 0 first
      requestAnimationFrame(() => requestAnimationFrame(doFade));
    } else {
      entering.onload = doFade;
    }

    return () => { entering.onload = null; };
  }, [src]); // eslint-disable-line react-hooks/exhaustive-deps

  const baseStyle = {
    position: 'absolute', inset: 0,
    width: '100%', height: '100%',
    objectFit: 'cover', objectPosition: 'top',
    willChange: 'opacity',
  };

  return (
    <>
      <img ref={aRef} alt="timelapse frame" style={{ ...baseStyle, opacity: 0 }} />
      <img ref={bRef} alt="timelapse frame" style={{ ...baseStyle, opacity: 0 }} />
    </>
  );
}

function ExportPanel({ frames }) {
  const [watermark, setWatermark] = useState(true);
  const [exportType, setExportType] = useState(null);
  const [exportState, setExportState] = useState(null);

  const handleExportMP4 = () => {
    setExportType('mp4');
    setExportState('generating');
    setTimeout(() => setExportState('done'), 2000);
  };

  const handleExportBeforeAfter = async () => {
    if (frames.length < 2) return;
    setExportType('comparison');
    setExportState('generating');

    try {
      const loadImg = (url) => new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = url;
      });

      const [img1, img2] = await Promise.all([
        loadImg(frames[0].imageUrl),
        loadImg(frames[frames.length - 1].imageUrl),
      ]);

      const canvas = document.createElement('canvas');
      canvas.width = 800;
      canvas.height = 450;
      const ctx = canvas.getContext('2d');

      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(0, 0, 800, 450);
      if (img1) ctx.drawImage(img1, 0, 0, 390, 450);
      if (img2) ctx.drawImage(img2, 410, 0, 390, 450);

      ctx.fillStyle = 'white';
      ctx.fillRect(395, 0, 10, 450);

      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.fillRect(0, 0, 100, 36);
      ctx.fillRect(410, 0, 100, 36);
      ctx.fillStyle = 'white';
      ctx.font = 'bold 16px sans-serif';
      ctx.fillText('Before', 12, 24);
      ctx.fillText('After', 422, 24);

      if (watermark) {
        ctx.fillStyle = 'rgba(255,255,255,0.55)';
        ctx.font = '13px sans-serif';
        ctx.fillText('Glowlytix', 694, 436);
      }

      canvas.toBlob((blob) => {
        if (!blob) { setExportState(null); return; }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'glow-comparison.png';
        a.click();
        URL.revokeObjectURL(url);
        setExportState('done');
      });
    } catch {
      setExportState(null);
    }
  };

  const handleShareLink = () => {
    setExportType('link');
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      setExportState('copied');
      setTimeout(() => { setExportType(null); setExportState(null); }, 2500);
    }).catch(() => { setExportType(null); setExportState(null); });
  };

  if (frames.length === 0) return null;

  return (
    <div className="bg-white rounded-3xl border border-stone-100 shadow-sm p-5">
      <p className="font-sans text-xs font-semibold text-stone-400 uppercase tracking-widest mb-4">Export</p>

      {/* Watermark toggle */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-stone-100">
        <div>
          <p className="font-sans text-sm font-semibold text-stone-700">Watermark</p>
          <p className="font-sans text-xs text-stone-400 mt-0.5">Add Glowlytix badge to exports</p>
        </div>
        <button
          onClick={() => setWatermark(w => !w)}
          className={`relative w-12 h-6 rounded-full transition-all duration-300 ${watermark ? 'bg-[#5C8A78]' : 'bg-stone-200'}`}
        >
          <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-all duration-300 ${watermark ? 'left-6 translate-x-0.5' : 'left-0.5'}`} />
        </button>
      </div>

      <div className="space-y-2.5">
        {/* MP4 */}
        <button
          onClick={handleExportMP4}
          disabled={exportState === 'generating' && exportType === 'mp4'}
          className="w-full flex items-center gap-3 bg-stone-50 border border-stone-200 rounded-2xl px-4 py-3.5 active:bg-stone-100 transition-colors disabled:opacity-60"
        >
          <span className="text-xl flex-shrink-0">🎬</span>
          <div className="text-left flex-1 min-w-0">
            <p className="font-sans text-sm font-semibold text-stone-700">Export as MP4</p>
            <p className="font-sans text-xs text-stone-400">Time-lapse video of your skin journey</p>
          </div>
          {exportType === 'mp4' && exportState === 'generating' && (
            <span className="font-sans text-xs text-stone-400 flex-shrink-0">Generating…</span>
          )}
          {exportType === 'mp4' && exportState === 'done' && (
            <span className="font-sans text-xs font-semibold flex-shrink-0" style={{ color: '#5C8A78' }}>✓ Done</span>
          )}
        </button>

        {/* Before/After */}
        <button
          onClick={handleExportBeforeAfter}
          disabled={frames.length < 2 || (exportState === 'generating' && exportType === 'comparison')}
          className="w-full flex items-center gap-3 bg-stone-50 border border-stone-200 rounded-2xl px-4 py-3.5 active:bg-stone-100 transition-colors disabled:opacity-60"
        >
          <span className="text-xl flex-shrink-0">🖼️</span>
          <div className="text-left flex-1 min-w-0">
            <p className="font-sans text-sm font-semibold text-stone-700">Before &amp; After Image</p>
            <p className="font-sans text-xs text-stone-400">
              {frames.length < 2 ? 'Need at least 2 frames' : 'Side-by-side comparison photo'}
            </p>
          </div>
          {exportType === 'comparison' && exportState === 'generating' && (
            <span className="font-sans text-xs text-stone-400 flex-shrink-0">Creating…</span>
          )}
          {exportType === 'comparison' && exportState === 'done' && (
            <span className="font-sans text-xs font-semibold flex-shrink-0" style={{ color: '#5C8A78' }}>✓ Saved</span>
          )}
        </button>

        {/* Shareable link */}
        <button
          onClick={handleShareLink}
          className="w-full flex items-center gap-3 bg-stone-50 border border-stone-200 rounded-2xl px-4 py-3.5 active:bg-stone-100 transition-colors"
        >
          <span className="text-xl flex-shrink-0">🔗</span>
          <div className="text-left flex-1 min-w-0">
            <p className="font-sans text-sm font-semibold text-stone-700">Copy Shareable Link</p>
            <p className="font-sans text-xs text-stone-400">Share your progress timeline</p>
          </div>
          {exportType === 'link' && exportState === 'copied' && (
            <span className="font-sans text-xs font-semibold flex-shrink-0" style={{ color: '#5C8A78' }}>Copied!</span>
          )}
        </button>
      </div>
    </div>
  );
}

function TimelapseLockScreen({ onOpenPricing }) {
  return (
    <div className="h-full flex flex-col items-center justify-center px-8 text-center relative overflow-hidden bg-stone-50">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-20%] w-[70%] h-[50%] rounded-full bg-[#d1ece5] blur-3xl opacity-40" />
        <div className="absolute bottom-[-10%] right-[-20%] w-[70%] h-[60%] rounded-full bg-[#e6e0f8] blur-3xl opacity-40" />
      </div>

      {/* Blurred preview suggestion */}
      <div className="relative w-full max-w-xs mb-8">
        <div className="w-full aspect-video rounded-3xl bg-gradient-to-br from-teal-100 to-violet-100 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-center gap-3 opacity-30">
            {['📸', '📸', '📸', '📸', '📸'].map((e, i) => (
              <span key={i} className="text-3xl">{e}</span>
            ))}
          </div>
          <div className="absolute inset-0 backdrop-blur-md bg-white/30 rounded-3xl" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-white/90 shadow-lg flex items-center justify-center">
            <SafeIcon icon={FiLock} className="text-2xl text-stone-600" />
          </div>
        </div>
      </div>

      <span className="text-4xl mb-4">🎬</span>
      <h2 className="font-serif text-3xl text-stone-900 mb-3">Your Skin Timelapse</h2>
      <p className="font-sans text-sm text-stone-500 leading-relaxed mb-8 max-w-[280px]">
        Watch your skin transformation unfold day by day. Available on Glow and Radiant Pro.
      </p>

      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={onOpenPricing}
        className="px-8 py-4 rounded-full font-sans font-semibold text-white text-base shadow-lg"
        style={{ background: 'linear-gradient(135deg, #5C8A78, #7ecdc4)' }}
      >
        Unlock with Glow — $14.99/yr
      </motion.button>
      <p className="font-sans text-xs text-stone-400 mt-3">~$1.25/month · Cancel anytime</p>
    </div>
  );
}

export default function TimelapseView({ entries, onOpenPricing }) {
  const { can } = useSubscription();

  const sortedEntries = [...entries].sort((a, b) => new Date(a.date) - new Date(b.date));
  const earliest = sortedEntries[0]?.date || format(new Date(), 'yyyy-MM-dd');
  const latest = sortedEntries[sortedEntries.length - 1]?.date || format(new Date(), 'yyyy-MM-dd');

  const [from, setFrom] = useState(earliest);
  const [to, setTo] = useState(latest);
  const [frames, setFrames] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [speedIdx, setSpeedIdx] = useState(1);
  const [started, setStarted] = useState(false);
  const [displayFrame, setDisplayFrame] = useState(null);
  const [infoVisible, setInfoVisible] = useState(true);
  const intervalRef = useRef(null);
  const fadeDuration = getFadeDuration(SPEEDS[speedIdx].ms);

  useEffect(() => {
    if (!frames[current]) return;
    setInfoVisible(false);
    const t = setTimeout(() => { setDisplayFrame(frames[current]); setInfoVisible(true); }, fadeDuration * 500);
    return () => clearTimeout(t);
  }, [current, frames, fadeDuration]);

  useEffect(() => {
    clearInterval(intervalRef.current);
    if (playing && frames.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrent(c => { if (c >= frames.length - 1) { setPlaying(false); return c; } return c + 1; });
      }, SPEEDS[speedIdx].ms);
    }
    return () => clearInterval(intervalRef.current);
  }, [playing, speedIdx, frames.length]);

  const goTo = useCallback((idx) => {
    setCurrent(Math.max(0, Math.min(frames.length - 1, idx)));
  }, [frames.length]);

  if (!can('timelapse')) {
    return <TimelapseLockScreen onOpenPricing={onOpenPricing} />;
  }

  const buildFrames = () => {
    const result = sortedEntries
      .filter(e => {
        const d = parseISO(e.date);
        const hasPhoto = e.photos?.some(p => p.useInTimelapse) || e.photos?.[0] || e.imageUrl;
        return hasPhoto && isWithinInterval(d, { start: startOfDay(parseISO(from)), end: endOfDay(parseISO(to)) });
      })
      .map(e => {
        // Resolve the timelapse photo URL
        const url = e.photos?.find(p => p.useInTimelapse)?.url
          || e.photos?.[0]?.url
          || e.imageUrl;
        return { ...e, imageUrl: url };
      });
    setFrames(result);
    setCurrent(0);
    setDisplayFrame(result[0] || null);
    setPlaying(false);
    setStarted(true);
    setInfoVisible(true);
  };

  const frame = frames[current];
  const progress = frames.length > 1 ? current / (frames.length - 1) : 0;

  return (
    <div className="h-full flex flex-col bg-stone-50 overflow-y-auto pb-4">
      <div className="px-5 pt-6 pb-4 flex-shrink-0 flex items-center gap-3">
        <img
          src="https://media-manager-c.questera.ai/greta-media/1593c15ad88356866a2eabcebc8aef2c67d9fc5afee8d56439ced245a0c6c3417efffbd95aedd1b6f68107af9f17a1fd/images/aW1hZ2UvcG5n/44d2d46ca6ce6a8943aba646038b0408.png"
          alt="Glowlytix Logo"
          className="w-10 h-10 object-contain drop-shadow-sm flex-shrink-0"
        />
        <div>
          <p className="font-sans text-xs text-stone-400 uppercase tracking-widest font-medium mb-0.5">Visualize</p>
          <h1 className="font-serif text-3xl text-stone-900 leading-none">Timelapse</h1>
        </div>
      </div>

      <div className="px-5 mb-4">
        <div className="bg-white rounded-3xl border border-stone-100 shadow-sm p-4">
          <p className="font-sans text-xs font-semibold text-stone-500 mb-3 flex items-center gap-1.5">
            <SafeIcon icon={FiCalendar} className="text-stone-400" /> Select Date Range
          </p>
          <div className="flex gap-3 mb-4">
            <DateInput label="From" value={from} onChange={setFrom} min={earliest} max={to} />
            <DateInput label="To" value={to} onChange={setTo} min={from} max={latest} />
          </div>
          <div className="grid grid-cols-4 gap-2 mb-4">
            {[{ label: 'Last 7d', days: 7 }, { label: 'Last 30d', days: 30 }, { label: 'Last 90d', days: 90 }, { label: 'All time', days: null }].map(({ label, days }) => (
              <button key={label} onClick={() => {
                if (days === null) { setFrom(earliest); setTo(latest); }
                else { const end = new Date(latest); const start = new Date(end); start.setDate(start.getDate() - days); setFrom(format(start, 'yyyy-MM-dd')); setTo(latest); }
              }} className="py-2 rounded-2xl border text-xs font-sans text-center bg-stone-50 text-stone-500 border-stone-200 active:bg-stone-100 transition-colors leading-tight">
                {label}
              </button>
            ))}
          </div>
          <motion.button whileTap={{ scale: 0.97 }} onClick={buildFrames}
            className="w-full py-3.5 rounded-2xl font-sans font-semibold text-white text-sm flex items-center justify-center gap-2"
            style={{ background: 'linear-gradient(135deg, #C07A52 0%, #E8C97A 100%)' }}>
            <SafeIcon icon={FiZap} /> Create Timelapse
          </motion.button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {started && (
          <motion.div key="player" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }} className="px-5 flex flex-col gap-4">
            {frames.length === 0 ? (
              <div className="bg-white rounded-3xl border border-stone-100 shadow-sm flex flex-col items-center justify-center py-16 gap-3">
                <span className="text-4xl">📷</span>
                <p className="font-serif text-xl text-stone-400">No photos found</p>
                <p className="font-sans text-xs text-stone-300">Try a wider date range</p>
              </div>
            ) : (
              <>
                <div className="rounded-3xl overflow-hidden shadow-lg relative bg-black" style={{ height: 340 }}>
                  <DissolveImage src={frame?.imageUrl} fadeDuration={fadeDuration} />
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-5 py-4">
                      <div style={{ opacity: infoVisible ? 1 : 0, transition: `opacity ${fadeDuration * 0.6}s ease-in-out`, willChange: 'opacity' }}>
                        <p className="text-white font-serif text-2xl">{displayFrame ? format(parseISO(displayFrame.date), 'MMMM d, yyyy') : ''}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-white/70 font-sans text-xs">✦ Glow {displayFrame?.glowScore}/10</span>
                          {displayFrame?.tags?.length > 0 && <span className="text-white/60 font-sans text-xs">· {displayFrame.tags.slice(0, 2).join(', ')}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm px-3 py-1 rounded-full">
                      <span className="text-white font-sans text-xs font-medium">{current + 1} / {frames.length}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-3xl border border-stone-100 shadow-sm p-4">
                  <div className="relative h-2 bg-stone-100 rounded-full mb-4 cursor-pointer"
                    onClick={e => { const rect = e.currentTarget.getBoundingClientRect(); goTo(Math.round((e.clientX - rect.left) / rect.width * (frames.length - 1))); }}>
                    <div className="absolute inset-y-0 left-0 rounded-full"
                      style={{ width: `${progress * 100}%`, background: 'linear-gradient(90deg, #C07A52, #E8C97A)', transition: `width ${fadeDuration * 0.6}s ease-in-out` }} />
                    <div className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white border-2 shadow-md"
                      style={{ left: `calc(${progress * 100}% - 8px)`, borderColor: '#C07A52', transition: `left ${fadeDuration * 0.6}s ease-in-out` }} />
                  </div>
                  <div className="flex items-center justify-between">
                    <button onClick={() => goTo(0)} className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-stone-600 active:bg-stone-200"><SafeIcon icon={FiSkipBack} className="text-base" /></button>
                    <button onClick={() => goTo(current - 1)} className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-stone-600 active:bg-stone-200"><SafeIcon icon={FiChevronLeft} className="text-base" /></button>
                    <motion.button whileTap={{ scale: 0.93 }}
                      onClick={() => { if (current >= frames.length - 1) { setCurrent(0); setTimeout(() => setPlaying(true), 80); } else { setPlaying(p => !p); } }}
                      className="w-16 h-16 rounded-full flex items-center justify-center text-white shadow-lg"
                      style={{ background: 'linear-gradient(135deg, #C07A52 0%, #E8C97A 100%)' }}>
                      <SafeIcon icon={playing ? FiPause : FiPlay} className="text-2xl" />
                    </motion.button>
                    <button onClick={() => goTo(current + 1)} className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-stone-600 active:bg-stone-200"><SafeIcon icon={FiChevronRight} className="text-base" /></button>
                    <button onClick={() => goTo(frames.length - 1)} className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-stone-600 active:bg-stone-200"><SafeIcon icon={FiSkipForward} className="text-base" /></button>
                  </div>
                  <div className="flex items-center justify-center gap-2 mt-4">
                    <span className="font-sans text-xs text-stone-400 uppercase tracking-widest mr-1">Speed</span>
                    {SPEEDS.map((s, i) => (
                      <button key={s.label} onClick={() => setSpeedIdx(i)}
                        className={`px-3 py-1 rounded-full border text-xs font-sans font-medium transition-all active:scale-95 ${speedIdx === i ? 'bg-stone-900 text-white border-stone-900' : 'bg-stone-50 text-stone-500 border-stone-200'}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="overflow-x-auto pb-1">
                  <div className="flex gap-2" style={{ width: 'max-content' }}>
                    {frames.map((f, i) => (
                      <button key={f.id} onClick={() => { goTo(i); setPlaying(false); }}
                        className="relative flex-shrink-0 rounded-2xl overflow-hidden"
                        style={{ width: 64, height: 80, opacity: i === current ? 1 : 0.5, transition: 'opacity 0.4s ease', ...(i === current ? { outline: '2px solid #C07A52', outlineOffset: 2 } : {}) }}>
                        <img src={f.imageUrl} alt="" className="w-full h-full object-cover object-top" />
                        <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-1 py-0.5">
                          <p className="text-white font-sans text-xs text-center leading-tight">{format(parseISO(f.date), 'MMM d')}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <ExportPanel frames={frames} />
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {!started && (
        <div className="flex flex-col items-center justify-center py-16 text-stone-300 px-5">
          <span className="text-6xl mb-4">🎬</span>
          <p className="font-serif text-2xl text-stone-400 text-center">Select a date range</p>
          <p className="font-sans text-sm text-stone-300 mt-2 text-center">Then tap "Create Timelapse" to watch your skin journey</p>
        </div>
      )}
    </div>
  );
}